<template>
  <v-app>
    <v-main class="assistant-main" style="margin:0px;padding:0px!important;">
      <!-- Compact Header -->
      <v-app-bar
        color="orange darken-1"
        dark
        elevation="2"
        fixed
        height="64"
        class="assistant-header"
      >
        <v-container class="d-flex align-center pa-0">
          <v-avatar size="40" class="mr-3">
            <img v-if="agentAvatar && agentData" :src="agentAvatar" :alt="agentData.first_name" style="width: 100%; height: 100%; object-fit: cover;" />
            <v-icon v-else size="24" class="material-icons-round">smart_toy</v-icon>
          </v-avatar>
          <div class="flex-grow-1">
            <v-toolbar-title class="text-h6 font-weight-bold" v-if="agentData">
              {{ agentData.first_name }}'s AI Assistant
            </v-toolbar-title>
            <v-toolbar-title class="text-h6 font-weight-bold" v-else>
              AI Vehicle Assistant
            </v-toolbar-title>
            <div class="caption white--text opacity-80" v-if="agentData && agentData.business_name">
              {{ agentData.business_name }}
            </div>
          </div>
          <v-chip
            v-if="agentData"
            small
            color="green"
            text-color="white"
            class="mr-3"
          >
            <v-icon left small class="material-icons-round">circle</v-icon>
            Online
          </v-chip>
          <!-- <v-btn
            icon
            @click="showInfo = !showInfo"
            class="ml-2"
          >
            <v-icon class="material-icons-round">{{ showInfo ? 'expand_less' : 'info' }}</v-icon>
          </v-btn> -->
        </v-container>
      </v-app-bar>

      <!-- Main Chat Container -->
      <div class="chat-layout" style="margin-top: 64px; height: calc(100vh - 94px);">
        <!-- Loading State -->
        <div v-if="loading" class="loading-container">
          <div class="text-center">
            <v-progress-circular
              indeterminate
              color="orange"
              size="60"
              width="4"
            ></v-progress-circular>
            <div class="mt-4 text-h6 font-weight-medium">Connecting to {{ subdomain }}'s assistant...</div>
          </div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-container">
          <div class="text-center">
            <v-icon size="64" color="error" class="material-icons-round mb-4">error_outline</v-icon>
            <h3 class="text-h5 font-weight-bold mb-3">Assistant Unavailable</h3>
            <p class="text-body-1 text--secondary mb-4">{{ error }}</p>
            <v-btn color="orange" @click="loadAgentData" outlined>
              <v-icon left class="material-icons-round">refresh</v-icon>
              Try Again
            </v-btn>
          </div>
        </div>

        <!-- Chat Interface -->
        <div v-else class="chat-interface">
          <!-- Collapsible Info Panel -->
          <v-expand-transition>
            <div v-show="showInfo" class="info-panel">
              <v-card class="ma-3" elevation="2" style="border-radius: 12px;">
                <v-card-text class="pa-4">
                  <div class="d-flex align-center mb-3">
                    <v-avatar size="48" class="mr-3" color="orange lighten-4">
                      <img v-if="agentAvatar" :src="agentAvatar" :alt="agentData.first_name" style="width: 100%; height: 100%; object-fit: cover;" />
                      <v-icon v-else size="28" color="orange" class="material-icons-round">person</v-icon>
                    </v-avatar>
                    <div>
                      <h4 class="text-h6 font-weight-bold mb-1">{{ agentData.first_name }}</h4>
                      <div class="text-body-2 text--secondary">
                        {{ agentData.business_name || 'Vehicle Sales Professional' }}
                      </div>
                    </div>
                  </div>
                  <div v-if="agentData.ai_personality_summary" class="personality-summary">
                    <div class="text-body-2 text--secondary">
                      {{ agentData.ai_personality_summary }}
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </v-expand-transition>

          <!-- Chat Component -->
          <div class="chat-wrapper mt-3">
            <DigitalAssistantChat
              v-if="agentData && agentData.user_id"
              :key="`agent-chat-${agentData.user_id}-${chatKey}`"
              :domain="subdomain"
              :user-id="agentData.user_id"
              :user-name="agentData.first_name"
              :user-avatar="agentAvatar"
              :auto-start="true"
              :full-page="true"
              class="assistant-chat"
              @conversation-loaded="onConversationLoaded"
              @conversation-error="onConversationError"
            />

            <!-- Chat Loading State -->
            <div v-else-if="agentData && !conversationLoaded" class="chat-loading text-center pa-8">
              <v-progress-circular indeterminate color="orange" size="40" width="3"></v-progress-circular>
              <div class="mt-3 text-body-2 grey--text">Starting conversation...</div>
            </div>
          </div>
        </div>
      </div>
    </v-main>
  </v-app>
</template>

<script>
import DigitalAssistantChat from './DigitalAssistantChat.vue'

export default {
  name: 'AgentAssistant',
  components: {
    DigitalAssistantChat
  },
  data() {
    return {
      loading: true,
      error: null,
      agentData: null,
      subdomain: '',
      showInfo: false,
      defaultAvatar: '/img/default-avatar.png',
      chatKey: 0, // Force chat component re-render when needed
      conversationLoaded: false
    }
  },
  computed: {
    agentAvatar() {
      if (this.agentData && this.agentData.avatar) {
        const wasbiUrl = process.env.VUE_APP_WASBI_URL || 'https://autosyncx.s3.us-west-1.wasabisys.com/';
        return wasbiUrl  + this.agentData.avatar;
      }
      return this.defaultAvatar;
    }
  },
  mounted() {
    console.log('🎯 AgentAssistant mounted')
    this.extractSubdomain()
    this.loadAgentData()
  },
  methods: {
    extractSubdomain() {
      const hostname = window.location.hostname
      const parts = hostname.split('.')

      console.log('🌐 Extracting subdomain from:', hostname)

      // Extract subdomain (assuming format: subdomain.autosyncx.com)
      if (parts.length >= 3) {
        this.subdomain = parts[0]
      } else {
        // For development/testing, get from route params or URL
        this.subdomain = this.$route.params.subdomain ||
                        this.$route.query.subdomain ||
                        'demo'
      }

      console.log('📍 Subdomain extracted:', this.subdomain)
    },

    async loadAgentData() {
      this.loading = true
      this.error = null
      this.conversationLoaded = false

      console.log('📡 Loading agent data for subdomain:', this.subdomain)

      try {
        const response = await axios.get(`/api/agents/${this.subdomain}`)

        console.log('📨 Agent API response:', response.data)

        if (response.data.flag === 1) {
          this.agentData = response.data.data
          console.log('✅ Agent data loaded:', this.agentData)

          // Force chat component to re-initialize with new agent data
          this.chatKey++
        } else {
          this.error = response.data.message || 'Agent not found or assistant not active'
          console.warn('⚠️ Agent not found:', response.data)
        }
      } catch (error) {
        console.error('❌ Error loading agent data:', error)

        if (error.response) {
          if (error.response.status === 404) {
            this.error = 'This agent assistant is not available or has not been set up yet.'
          } else {
            this.error = error.response.data?.message || 'Unable to load agent assistant. Please try again later.'
          }
        } else if (error.request) {
          this.error = 'Network error. Please check your connection and try again.'
        } else {
          this.error = 'An unexpected error occurred. Please try again later.'
        }
      } finally {
        this.loading = false
      }
    },

    onConversationLoaded() {
      console.log('✅ Conversation loaded successfully')
      this.conversationLoaded = true
    },

    onConversationError(error) {
      console.error('❌ Conversation error:', error)
      // Don't show error immediately, let the chat component handle retries
    },

    // Clean method to refresh the entire assistant
    refreshAssistant() {
      console.log('🔄 Refreshing assistant...')
      this.chatKey++
      this.loadAgentData()
    }
  }
}
</script>

<style scoped>
/* Main Layout */
.assistant-main {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  overflow: hidden;
}

.assistant-header {
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

/* Chat Layout */
.chat-layout {
  display: flex;
  flex-direction: column;
  background: transparent;
}

.chat-interface {
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* Loading and Error States */
.loading-container,
.error-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
}

/* Info Panel */
.info-panel {
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.personality-summary {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
  padding: 0.75rem;
  border-radius: 8px;
  border-left: 3px solid #ff9800;
  margin-top: 0.75rem;
}

/* Chat Wrapper */
.chat-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0; /* Important for flex child to shrink */
}

.assistant-chat {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* Online Status */
.opacity-80 {
  opacity: 0.8;
}

/* Hide any debugging elements */
.assistant-main .chat-toggle-btn,
[data-testid*="read"],
[class*="read-data"],
[id*="read-data"],
button[title*="Read Data"],
.debug-button,
.dev-tools-button {
  display: none !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .assistant-header {
    height: 56px !important;
  }

  .chat-layout {
    margin-top: 56px !important;
    height: calc(100vh - 56px) !important;
  }

  .loading-container,
  .error-container {
    padding: 1rem;
  }

  .info-panel {
    margin: 0.5rem;
  }
}

@media (max-width: 480px) {
  .assistant-header .v-toolbar__title {
    font-size: 1rem !important;
  }

  .assistant-header .caption {
    font-size: 0.7rem !important;
  }
}

/* Smooth Animations */
.v-expand-transition-enter-active,
.v-expand-transition-leave-active {
  transition: all 0.3s ease;
}

.loading-container .v-progress-circular {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.7; }
  100% { opacity: 1; }
}

/* Clean box model */
.assistant-main * {
  box-sizing: border-box;
}

/* Ensure proper scrolling */
.chat-interface {
  overflow: hidden;
}
</style>
